import { Consumption_ConsumptionRealDetailExportEnnB, Consumption_ConsumptionRealDetailExportEvn, Consumption_ConsumptionRealDetailExportPvo } from "data/endpoints";
import { formatISO, isBefore, startOfDay, sub } from "date-fns";
import { CommodityDetail } from "features/contracts/enums/commodityDetail";
import { ContractType } from "features/contracts/enums/contractType";

export const getMeasuredDataParams = (
  identificationParams: URLSearchParams,
  year: number,
  month?: number,
  isProduction?: boolean,
  graphDataParams?: URLSearchParams,
  downloadDataParams?: URLSearchParams
): URLSearchParams => {
  let params = new URLSearchParams(identificationParams);

  let from;
  let to;

  if (month) {
    from = new Date(year, month - 1, 1);
    to = new Date(year, month, 0);
  } else {
    from = new Date(year, 0, 1);
    to = new Date(year, 11, 31);
  }

  params.set("From", formatISO(from, { representation: "date" }));
  params.set("To", formatISO(to, { representation: "date" }));

  if (isProduction) {
    params.set("IsProduction", `${isProduction}`);
  }

  setOptionalParams(params, graphDataParams);
  setOptionalParams(params, downloadDataParams);

  return params;
};

export const getActualOrEndYear = (to: Date): number => {
  let actualYear = new Date().getFullYear();
  let endYear = new Date(to).getFullYear();
  return endYear > actualYear ? actualYear : endYear;
};

export const getMonthBoundaries = (
  from: Date,
  to: Date,
  selectedYear: number
): number[] => {
  let startMonth = 1;
  let endMonth = 12;
  let fromDateParsed = new Date(from);
  let toDateParsed = new Date(to);

  if (fromDateParsed.getFullYear() === selectedYear) {
    startMonth = fromDateParsed.getMonth() + 1;
  }
  if (toDateParsed.getFullYear() === selectedYear) {
    endMonth = toDateParsed.getMonth() + 1;
  }

  return [startMonth, endMonth];
};

export const getEndDate = (to: Date, previousMonthsCount: number = 0): Date => {
  let actualDate = startOfDay(new Date());
  let toDate = startOfDay(new Date(to));
  let endDate: Date;

  if (isBefore(actualDate, toDate)) {
    // aktualni datum je pred koncovym datem (nastavime jako koncove (minus pocet mesicu zpet))
    endDate = sub(actualDate, {months: previousMonthsCount });
  } else {
    // koncove datume je pred aktualnim datem (nastavime jako koncove)
    endDate = toDate;
  }
  return endDate;
}

export const getDownloadUrlByContractType = (
  contractType: ContractType,
  isProduction?: boolean,
  meterType?: string
) => {
  let url = "";

  if (contractType === "ElectricityHighVoltage") {
    url = Consumption_ConsumptionRealDetailExportEvn;
  } else if (contractType === "GasLargeConsumption") {
    url = Consumption_ConsumptionRealDetailExportPvo;
  } else if (contractType === "ElectricityLowVoltage" && meterType === "B") {
    url = Consumption_ConsumptionRealDetailExportEnnB;
  }

  return url;
};

const setOptionalParams = (
  params: URLSearchParams,
  optionalParams?: URLSearchParams
) => {
  if (optionalParams) {
    for (const [key, value] of optionalParams.entries()) {
      params.set(key, value);
    }
  }
};

export const getMeasuredDataType = (
  commodityDetail: CommodityDetail,
  isProduction: boolean = false
): string => {
  let tanslationKey = "";

  switch (commodityDetail) {
    case "ElectricityCustomer":
    case "ElectricitySupplier":
      tanslationKey = isProduction ? "production" : "consumption";
      break;
    case "GasCustomer":
    case "GasSupplier":
      tanslationKey = "consumption";
      break;
    case "HeatCustomer":
      tanslationKey = "consumption";
      break;
    case "eMobility":
      tanslationKey = "charging";
      break;
    case "CNG":
      tanslationKey = "fueling";
      break;
    default:
      tanslationKey = "";
      break;
  }

  return tanslationKey;
};

export const hasMeasuredData = (
  contractType: ContractType,
  meterType: string
): boolean => {
  const enabledContractTypes: ContractType[] = [
    "ElectricityHighVoltage",
    "GasLargeConsumption",
  ];
  const enabledMeterTypes: string[] = ["B", "C1", "C2", "C3"];

  if (enabledContractTypes.includes(contractType)) {
    return true;
  }

  if (
    contractType === "ElectricityLowVoltage" &&
    enabledMeterTypes.includes(meterType)
  ) {
    return true;
  }

  return false;
};
